import React, { useState, useEffect, useCallback } from "react";
import { InfoIcon } from "../common/Icons";
import "./SmartBatchingModal.css"; // Use the same CSS as TE modal
import { API_BASE } from "../../constants/urls";

const CESmartBatchingModal = ({
    isOpen,
    onClose,
    selectedSubmissions = [],
    availableCEs = [],
    onAssignmentComplete
}) => {
    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isAssigningAll, setIsAssigningAll] = useState(false);
    const [assignmentStatus, setAssignmentStatus] = useState({}); // { batchIndex: 'pending' | 'loading' | 'completed' | 'failed' }
    const [error, setError] = useState(null);


    const fetchSmartSuggestions = useCallback(async () => {
        if (!selectedSubmissions || selectedSubmissions.length === 0) return;

        setLoading(true);
        setError(null);
        try {
            const articleIds = selectedSubmissions.map(s => s.article_id);
            const response = await fetch(`${API_BASE}/api/ce-assignments/smart-batches`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ article_ids: articleIds })
            });

            const data = await response.json();
            if (response.ok && data.success) {
                // Map backend buckets to frontend batches structure with enhanced naming
                const baseTimestamp = Date.now();
                const suggestedBatches = data.buckets.map((bucket, i) => {
                    // Generate unique batch name with random suffix to prevent collisions
                    const randomSuffix = Math.random().toString(36).substring(2, 8).toUpperCase();
                    const batchName = bucket.suggested_batch_name ||
                        `CE_Batch_${bucket.level.toUpperCase()}_${baseTimestamp}_${i}_${randomSuffix}`;

                    return {
                        id: `batch-${baseTimestamp}-${i}-${randomSuffix}`,
                        name: batchName,
                        submissions: bucket.articles.map(article => {
                            // Match with original selectedSubmissions object to get more data if needed
                            const original = selectedSubmissions.find(s => s.article_id === article.article_id);
                            return { ...original, ...article };
                        }),
                        ceId: bucket.suggested_ce ? bucket.suggested_ce.id : "",
                        allowedCEs: bucket.allowed_ces || [],
                        level: bucket.level,
                        priority_order: bucket.priority_order || 4,
                        batch_count: bucket.batch_count || 1,
                        article_count: bucket.article_count || bucket.articles.length,
                        journal_codes: bucket.journal_codes || [],
                        notes: "",
                        page_count: bucket.total_pages || 0,
                    };
                });
                console.log("suggestedBatches", suggestedBatches);
                setBatches(suggestedBatches);

                const initialStatus = {};
                suggestedBatches.forEach((_, index) => {
                    initialStatus[index] = 'pending';
                });
                setAssignmentStatus(initialStatus);
            } else {
                setError(data.error || "Failed to fetch smart suggestions");
            }
        } catch (err) {
            console.error("Error fetching smart suggestions:", err);
            setError("Network error fetching suggestions");
        } finally {
            setLoading(false);
        }
    }, [selectedSubmissions]);

    useEffect(() => {
        if (isOpen) {
            fetchSmartSuggestions();
        }
    }, [isOpen, fetchSmartSuggestions]);

    const addEmptyBatch = () => {
        const timestamp = Date.now();
        const randomSuffix = Math.random().toString(36).substring(2, 8).toUpperCase();
        const newBatch = {
            id: `batch-${timestamp}-${randomSuffix}`,
            name: `CE_Batch_Manual_${timestamp}_${randomSuffix}`,
            submissions: [],
            ceId: "",
            allowedCEs: availableCEs, // All CEs allowed for manual batch until articles added
            level: "manual",
            priority_order: 5,
            batch_count: 1,
            article_count: 0,
            journal_codes: [],
            page_count: 0,
            notes: ""
        };
        setBatches([...batches, newBatch]);
        setAssignmentStatus(prev => ({ ...prev, [batches.length]: 'pending' }));
    };

    const moveArticle = (articleId, fromBatchIndex, toBatchIndex) => {
        const newBatches = [...batches];
        const article = newBatches[fromBatchIndex].submissions.find(s => s.article_id === articleId);

        // Remove from source
        newBatches[fromBatchIndex].submissions = newBatches[fromBatchIndex].submissions.filter(s => s.article_id !== articleId);

        // Add to destination
        newBatches[toBatchIndex].submissions.push(article);

        setBatches(newBatches);
    };

    const handleBatchCEChange = (index, ceId) => {
        const updatedBatches = [...batches];
        updatedBatches[index].ceId = ceId;
        setBatches(updatedBatches);
    };

    // Filter CEs based on batch level and assignment priority
    const getFilteredCEs = (batchLevel) => {
        if (!batchLevel || !availableCEs) return availableCEs;

        return availableCEs.filter(ce => {
            const ceLevel = ce.ce_level || ce.ceLevel || 'non_critical';

            switch (batchLevel.toLowerCase()) {
                case 'premium':
                    return ceLevel === 'premium';
                case 'critical':
                    return ceLevel === 'critical';
                case 'intermediate':
                    return ['intermediate', 'non_critical'].includes(ceLevel); // Only intermediate and non_critical CEs
                case 'non_critical':
                    return ['intermediate', 'non_critical'].includes(ceLevel); // Only intermediate and non_critical CEs
                default:
                    return true;
            }
        });
    };

    // Split batch into smaller sub-batches
    const splitBatch = (batchIndex, numberOfSplits) => {
        const batch = batches[batchIndex];
        const submissions = batch.submissions || [];

        if (submissions.length <= numberOfSplits) {
            alert("Cannot split: batch has fewer articles than requested splits");
            return;
        }

        const itemsPerSplit = Math.ceil(submissions.length / numberOfSplits);
        const newBatches = [...batches];

        // Remove original batch
        newBatches.splice(batchIndex, 1);

        // Create new sub-batches
        for (let i = 0; i < numberOfSplits; i++) {
            const startIndex = i * itemsPerSplit;
            const endIndex = Math.min(startIndex + itemsPerSplit, submissions.length);
            const subBatchSubmissions = submissions.slice(startIndex, endIndex);

            if (subBatchSubmissions.length > 0) {
                const subBatch = {
                    ...batch,
                    id: `${batch.id}_split_${i + 1}`,
                    name: `${batch.name}_Split_${i + 1}`,
                    submissions: subBatchSubmissions,
                    ceId: '', // Reset CE selection for new batches
                    notes: `${batch.notes || ''} (Split ${i + 1}/${numberOfSplits})`.trim()
                };

                // Insert at the position where original batch was
                newBatches.splice(batchIndex + i, 0, subBatch);
            }
        }

        setBatches(newBatches);

        // Update assignment status for new batches
        const newStatus = { ...assignmentStatus };
        delete newStatus[batchIndex]; // Remove original batch status

        // Add status for new sub-batches
        for (let i = 0; i < numberOfSplits; i++) {
            newStatus[batchIndex + i] = 'pending';
        }
        setAssignmentStatus(newStatus);
    };

    const handleBatchNotesChange = (index, notes) => {
        const updatedBatches = [...batches];
        updatedBatches[index].notes = notes;
        setBatches(updatedBatches);
    };

    const assignBatch = async (index) => {
        const batch = batches[index];
        if (!batch.ceId) {
            alert("Please select a CE for this batch.");
            return;
        }

        setAssignmentStatus(prev => ({ ...prev, [index]: 'loading' }));

        try {
            const response = await fetch(`${API_BASE}/api/ce-assignments/create-from-te-submissions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    batch_name: batch.name,
                    te_submission_ids: batch.submissions.map(s => s.article_id),
                    ce_id: parseInt(batch.ceId),
                    notes: batch.notes
                })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setAssignmentStatus(prev => ({ ...prev, [index]: 'completed' }));
                return true;
            } else {
                setAssignmentStatus(prev => ({ ...prev, [index]: 'failed' }));
                console.error("Assignment failed:", data.error);
                return false;
            }
        } catch (error) {
            setAssignmentStatus(prev => ({ ...prev, [index]: 'failed' }));
            console.error("Error assigning batch:", error);
            return false;
        }
    };

    const assignAll = async () => {
        const unassignedBatches = batches.map((b, i) => ({ batch: b, index: i }))
            .filter(item => assignmentStatus[item.index] !== 'completed');

        if (unassignedBatches.some(item => !item.batch.ceId)) {
            alert("Please select a CE for all batches before assigning all.");
            return;
        }

        setIsAssigningAll(true);
        let allSuccess = true;

        for (const item of unassignedBatches) {
            const success = await assignBatch(item.index);
            if (!success) allSuccess = false;
        }

        setIsAssigningAll(false);
        if (allSuccess) {
            onAssignmentComplete();
        } else {
            alert("Some batches failed to assign. Please check the status indicators.");
        }
    };

    if (!isOpen) return null;

    return (
        <div className="smart-batching-modal-overlay">
            <div className="smart-batching-modal">
                <div className="modal-header">
                    <h2>Smart Batching - CE Assignment</h2>
                    <button className="close-button" onClick={onClose}>
                        ×
                    </button>
                </div>

                {/* CE Capacity Information Header */}
                <div className="te-capacity-info-header">
                    <div className="capacity-info-title">
                        <InfoIcon className="w-5 h-5" />
                        <span>CE Assignment Overview</span>
                    </div>
                    <div className="capacity-info-list">
                        <div className="capacity-info-item">
                            <span className="te-name">Selected Articles:</span>
                            <span className="te-capacity">{selectedSubmissions.length}</span>
                        </div>
                        <div className="capacity-info-item">
                            <span className="te-name">Smart Batches:</span>
                            <span className="te-capacity">{batches.length}</span>
                        </div>
                        <div className="capacity-info-item">
                            <span className="te-name">Available CEs:</span>
                            <span className="te-capacity">{availableCEs.length}</span>
                        </div>
                    </div>
                    <p className="capacity-info-note">
                        Articles are intelligently grouped based on complexity and journal type for optimal CE assignment.
                    </p>
                </div>

                {/* Batching Controls */}
                <div className="batching-controls">
                    <button
                        className="refresh-batches-btn"
                        onClick={addEmptyBatch}
                        disabled={loading}
                    >
                        ➕ Add New Batch
                    </button>

                    <button
                        className="refresh-batches-btn"
                        onClick={fetchSmartSuggestions}
                        disabled={loading}
                    >
                        {loading ? "Loading..." : "🎯 Reset to Smart Suggestions"}
                    </button>
                </div>

                {error && (
                    <div className="assignment-results" style={{ background: '#fef2f2', borderColor: '#fecaca' }}>
                        <h3 style={{ color: '#dc2626' }}>Error</h3>
                        <p style={{ color: '#dc2626', fontWeight: 'normal' }}>{error}</p>
                    </div>
                )}

                {/* Batch List */}
                {batches.length > 0 && (
                    <div className="batches-container">
                        <h3>Batches ({batches.length})</h3>
                        <div className="batches-grid">
                            {batches.map((batch, index) => (
                                <CEBatchCard
                                    key={batch.id || index}
                                    batch={batch}
                                    batchIndex={index}
                                    availableCEs={availableCEs}
                                    filteredCEs={getFilteredCEs(batch.level)}
                                    allBatches={batches}
                                    onCESelection={handleBatchCEChange}
                                    onAssignBatch={assignBatch}
                                    onMoveArticle={moveArticle}
                                    onNotesChange={handleBatchNotesChange}
                                    onSplitBatch={splitBatch}
                                    assignmentStatus={assignmentStatus[index]}
                                />
                            ))}
                        </div>
                    </div>
                )}
                {/* Footer Actions */}
                <div className="modal-footer">
                    <button
                        className="assign-all-btn"
                        onClick={assignAll}
                        disabled={isAssigningAll || batches.length === 0}
                    >
                        {isAssigningAll ? "Assigning..." : `Assign All (${batches.length} batches)`}
                    </button>

                    <button className="cancel-btn" onClick={onClose}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

// CE Batch Card Component (similar to TE BatchCard but for CE assignments)
const CEBatchCard = ({
    batch,
    batchIndex,
    availableCEs,
    filteredCEs,
    allBatches,
    onCESelection,
    onAssignBatch,
    onMoveArticle,
    onNotesChange,
    onSplitBatch,
    assignmentStatus,
}) => {
    // Calculate total page count for this batch
    console.log(batch, 'batch')

    const calculateBatchPageCount = (batch) => {
        console.log(batch,'batch')
        if (!batch.submissions || batch.submissions.length === 0) return 0;
        return batch.page_count.toFixed(2);
        // return batch.submissions.reduce((total, submission) => {
        //     const wordCount = submission.page_count || 0;
        //     const pageCount = wordCount > 0 ? wordCount / 400 : 0;
        //     return total + pageCount;
        // }, 0);
    };
    const [selectedArticles, setSelectedArticles] = React.useState(new Set());
    const [showMoveOptions, setShowMoveOptions] = React.useState(false);
    const [showSplitOptions, setShowSplitOptions] = React.useState(false);

    const handleArticleSelect = (articleId) => {
        const newSelected = new Set(selectedArticles);
        if (newSelected.has(articleId)) {
            newSelected.delete(articleId);
        } else {
            newSelected.add(articleId);
        }
        setSelectedArticles(newSelected);
    };

    const handleMoveSelectedArticles = (toBatchIndex) => {
        selectedArticles.forEach((articleId) => {
            onMoveArticle(articleId, batchIndex, toBatchIndex);
        });
        setSelectedArticles(new Set());
        setShowMoveOptions(false);
    };

    return (
        <div
            className={`batch-card ${assignmentStatus || "pending"} ${batch.level === "high" ? "premium-batch" :
                batch.level === "medium" ? "validation-query-batch" : ""
                }`}
        >
            <div className="batch-header">
                <h4>
                    Batch {batchIndex + 1}
                    {batch.mixed_levels && batch.mixed_levels.length > 1 ? (
                        <span className="mixed-levels-badge"> 🔄 Mixed ({batch.mixed_levels.join(', ')})</span>
                    ) : (
                        <>
                            {batch.level === "premium" && (
                                <span className="premium-badge"> ⭐ Premium</span>
                            )}
                            {batch.level === "critical" && (
                                <span className="critical-badge"> 🔴 Critical</span>
                            )}
                            {batch.level === "intermediate" && (
                                <span className="intermediate-badge"> 🟡 Intermediate</span>
                            )}
                            {batch.level === "non_critical" && (
                                <span className="non-critical-badge"> 🟢 Non-Critical</span>
                            )}
                        </>
                    )}
                </h4>
                <div className="batch-header-actions">
                    <span className="article-count">
                        {batch.submissions?.length || 0} articles
                        ({calculateBatchPageCount(batch)} pages)
                    </span>
                    {(batch.submissions?.length || 0) > 1 && assignmentStatus !== 'completed' && (
                        <button
                            className="split-batch-btn"
                            onClick={() => setShowSplitOptions(!showSplitOptions)}
                            title="Split this batch into smaller batches"
                        >
                            ✂️ Split
                        </button>
                    )}
                </div>
            </div>

            {/* Split Options */}
            {showSplitOptions && (
                <div className="split-options">
                    <p>Split into how many batches?</p>
                    <div className="split-buttons">
                        {[2, 3, 4, 5].map(num => (
                            <button
                                key={num}
                                className="split-option-btn"
                                onClick={() => {
                                    onSplitBatch(batchIndex, num);
                                    setShowSplitOptions(false);
                                }}
                                disabled={batch.submissions?.length <= num}
                            >
                                {num} batches
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="te-selection">
                <label>
                    Assign to CE:
                    <span className="level-filter-info">
                        ({batch.level?.toUpperCase() || 'UNKNOWN'} Level - {filteredCEs?.length || 0} available)
                    </span>
                </label>
                <select
                    value={batch.ceId || ""}
                    onChange={(e) => onCESelection(batchIndex, e.target.value)}
                    disabled={assignmentStatus === 'completed' || assignmentStatus === 'loading'}
                >
                    <option value="">Select a CE...</option>
                    {filteredCEs?.map((ce) => {
                        const ceLevel = ce.ce_level || ce.ceLevel || 'non_critical';
                        const capacity = ce.daily_capacity_pages || ce.dailyCapacity || 0;
                        const currentPageLoad = ce.currentPageLoad || 0;
                        const availableCapacity = ce.availablePageCapacity || capacity;
                        const utilization = ce.capacityUtilization || 0;
                        const isAtCapacity = ce.isAtCapacity || currentPageLoad >= capacity;

                        return (
                            <option
                                key={ce.id}
                                value={ce.id}
                                disabled={isAtCapacity}
                            >
                                {ce.username} ({ceLevel.toUpperCase()}) - {currentPageLoad}/{capacity} pages ({utilization.toFixed(1)}%)
                                {isAtCapacity ? ' [AT CAPACITY]' : availableCapacity < 5 ? ' [NEAR CAPACITY]' : ''}
                            </option>
                        );
                    })}
                </select>
                {filteredCEs?.length === 0 && (
                    <p className="no-ces-warning">
                        ⚠️ No CEs available for {batch.level} level articles
                    </p>
                )}
            </div>

            {/* Notes Section */}
            <div className="te-selection">
                <label>Notes (Optional):</label>
                <textarea
                    value={batch.notes || ""}
                    onChange={(e) => onNotesChange(batchIndex, e.target.value)}
                    placeholder="Add notes for this batch..."
                    disabled={assignmentStatus === 'completed' || assignmentStatus === 'loading'}
                    style={{
                        width: '100%',
                        minHeight: '60px',
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '0.9rem',
                        resize: 'vertical'
                    }}
                />
            </div>

            <div className="articles-list">
                <div className="articles-header">
                    <span>Articles</span>
                    {selectedArticles.size > 0 && (
                        <div className="article-actions">
                            <button
                                className="move-articles-btn"
                                onClick={() => setShowMoveOptions(!showMoveOptions)}
                            >
                                Move ({selectedArticles.size})
                            </button>
                            {showMoveOptions && (
                                <div className="move-options">
                                    {allBatches.map(
                                        (targetBatch, targetIndex) =>
                                            targetIndex !== batchIndex && (
                                                <button
                                                    key={targetIndex}
                                                    className="move-option"
                                                    onClick={() =>
                                                        handleMoveSelectedArticles(targetIndex)
                                                    }
                                                >
                                                    → Batch {targetIndex + 1}
                                                </button>
                                            )
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {batch.submissions && batch.submissions.length > 0 ? (
                    batch.submissions.map((submission) => (
                        <div
                            key={submission.article_id}
                            className={`article-item ${selectedArticles.has(submission.article_id) ? "selected" : ""
                                }`}
                            onClick={() => handleArticleSelect(submission.article_id)}
                        >
                            <input
                                type="checkbox"
                                checked={selectedArticles.has(submission.article_id)}
                                onChange={() => handleArticleSelect(submission.article_id)}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <span className="article-id">{submission.article_id}</span>
                            <span className="article-journal">{submission.journal_code || 'N/A'}</span>
                            <span className="article-pages">
                                {submission.page_count ? `${submission.page_count} pages` : '0 pages'}
                            </span>
                        </div>
                    ))
                ) : (
                    <div className="empty-batch-message">
                        <span>No articles in this batch</span>
                    </div>
                )}
            </div>

            <div className="batch-actions">
                <button
                    className="assign-batch-btn"
                    onClick={() => onAssignBatch(batchIndex)}
                    disabled={
                        assignmentStatus === "loading" ||
                        assignmentStatus === "completed" ||
                        !batch.ceId ||
                        !batch.submissions ||
                        batch.submissions.length === 0
                    }
                >
                    {assignmentStatus === "loading" ? (
                        "Assigning..."
                    ) : assignmentStatus === "completed" ? (
                        "Assigned ✓"
                    ) : assignmentStatus === "failed" ? (
                        "Failed ✕"
                    ) : !batch.submissions || batch.submissions.length === 0 ? (
                        "No Articles"
                    ) : (
                        "Assign Batch"
                    )}
                </button>
            </div>
        </div>
    );
};

export default CESmartBatchingModal;
